import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";

// The seven things a shift assignment can name. Order mirrors the backend
// precedence ladder, most specific first, so the picker reads the same way
// the resolution rule does.
export const TARGET_TYPES = [
    { value: "employee", label: "Employee" },
    { value: "group", label: "Group" },
    { value: "position", label: "Position" },
    { value: "department", label: "Department" },
    { value: "branch", label: "Branch" },
    { value: "employment_type", label: "Employment type" },
    { value: "company", label: "Whole company" },
];

const EMPLOYMENT_TYPES = ["regular", "probationary", "contractor", "project", "daily"];

const listOf = (r) => (Array.isArray(r.data) ? r.data : r.data.items || []);

// The per-type id->name lookups, lifted out of the picker so anything that
// needs to *display* a target (not just pick one — see ShiftAssignments'
// table) can resolve the same names without duplicating these seven branches.
// Always resolves to an array; a failed call degrades to [] rather than
// throwing, same contract the picker itself relied on.
export async function loadTargetOptions(targetType) {
    try {
        if (targetType === "employee") {
            const r = await api.get("/hris/employees", { params: { limit: 1000 } });
            return listOf(r).map((e) => ({ id: e.id, name: `${e.first_name} ${e.last_name}` }))
                .sort((a, b) => a.name.localeCompare(b.name));
        }
        if (targetType === "department") {
            const r = await api.get("/hris/departments");
            return listOf(r).map((d) => ({ id: d.id, name: d.name }));
        }
        if (targetType === "branch") {
            const r = await api.get("/hris/branches");
            return listOf(r).map((b) => ({ id: b.id, name: b.name }));
        }
        if (targetType === "group") {
            const r = await api.get("/employee-groups");
            return listOf(r).map((g) => ({ id: g.id, name: g.name }));
        }
        if (targetType === "position") {
            const r = await api.get("/hris/employees", { params: { limit: 1000 } });
            return [...new Set(listOf(r).map((e) => e.position).filter(Boolean))].map((p) => ({ id: p, name: p }));
        }
        if (targetType === "employment_type") {
            return EMPLOYMENT_TYPES.map((t) => ({ id: t, name: t }));
        }
        if (targetType === "company") {
            const r = await api.get("/hris/companies");
            return listOf(r).map((c) => ({ id: c.id, name: c.name }));
        }
        return [];
    } catch {
        return [];
    }
}

export default function TargetPicker({ targetType, targetId, onChange }) {
    const [options, setOptions] = useState([]);

    useEffect(() => {
        let cancelled = false;
        loadTargetOptions(targetType).then((rows) => { if (!cancelled) setOptions(rows); });
        return () => { cancelled = true; };
    }, [targetType]);

    return (
        <div className="grid grid-cols-2 gap-2">
            <div>
                <Label>Assign to</Label>
                <select className="h-9 w-full rounded-md border px-2 text-sm bg-background"
                        value={targetType} data-testid="target-type"
                        onChange={(e) => onChange(e.target.value, "")}>
                    {TARGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
            </div>
            <div>
                <Label>Which one</Label>
                <select className="h-9 w-full rounded-md border px-2 text-sm bg-background"
                        value={targetId} data-testid="target-id"
                        onChange={(e) => onChange(targetType, e.target.value)}>
                    <option value="">Select…</option>
                    {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
            </div>
        </div>
    );
}
